import React, { useMemo, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getBotReply = (message) => {
  const text = message.toLowerCase();
  if (text.includes('refund')) {
    return 'Refunds can be requested within 6 hours of checkout. Approved refunds are processed within 7 business days (timing depends on your financial provider).';
  }
  if (text.includes('pickup') || text.includes('portmore') || text.includes('kingston') || text.includes('halfway')) {
    return 'Free pickup is available in Portmore (St. Catherine) or Kingston (Half Way Tree). Choose your preferred pickup option after checkout.';
  }
  if (text.includes('delivery') || text.includes('knutsford')) {
    return 'Paid delivery is available in Jamaica (e.g., Knutsford or similar providers). You can select a delivery provider after checkout.';
  }
  if (text.includes('courier') || text.includes('international') || text.includes('usa') || text.includes('united')) {
    return 'For international orders, please tell us your preferred courier provider. We will coordinate shipping details.';
  }
  return 'Thanks for reaching out. Ask about delivery, pickup, refunds, or international shipping.';
};

export function SupportChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [messages, setMessages] = useState([
    { from: 'bot', text: 'Hi! How can I help you today?' }
  ]);

  const canSend = useMemo(() => input.trim().length > 0, [input]);

  const handleSend = () => {
    if (!canSend) return;
    const userMsg = input.trim();
    setMessages((prev) => [...prev, { from: 'user', text: userMsg }]);
    setInput('');

    const reply = getBotReply(userMsg);
    setTimeout(() => {
      setMessages((prev) => [...prev, { from: 'bot', text: reply }]);
    }, 300);
  };

  const handleContactSupport = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Please add your name and email to contact support.');
      return;
    }

    const transcript = messages.map((m) => `${m.from === 'user' ? 'Customer' : 'Support'}: ${m.text}`).join('\n');

    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        subject: 'Support Chat Request',
        message: transcript
      });
      toast.success('Support message sent. We will reply by email.');
    } catch (error) {
      console.error('Support chat error:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {open && (
        <div className="w-80 sm:w-96 bg-white border border-cream-200 rounded-2xl shadow-xl overflow-hidden mb-4">
          <div className="flex items-center justify-between px-4 py-3 bg-cream-100">
            <div className="font-heading text-leather-900">Belt & Tingz Support</div>
            <button onClick={() => setOpen(false)} className="text-leather-600 hover:text-leather-900">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-4 py-3 space-y-3 max-h-72 overflow-y-auto">
            {messages.map((msg, idx) => (
              <div key={idx} className={msg.from === 'user' ? 'text-right' : 'text-left'}>
                <span className={msg.from === 'user'
                  ? 'inline-block bg-leather-900 text-white text-sm px-3 py-2 rounded-2xl'
                  : 'inline-block bg-cream-100 text-leather-800 text-sm px-3 py-2 rounded-2xl'}>
                  {msg.text}
                </span>
              </div>
            ))}
          </div>

          <div className="px-4 pb-3">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                className="input"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <input
                className="input"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <input
                className="flex-1 input"
                placeholder="Type a message"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              />
              <button className="btn-primary" onClick={handleSend}>
                Send
              </button>
            </div>
            <button className="mt-2 text-xs text-leather-600 hover:text-leather-900" onClick={handleContactSupport}>
              Send transcript to support
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-14 h-14 rounded-full bg-leather-900 text-white flex items-center justify-center shadow-lg hover:bg-leather-800"
        aria-label="Open support chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
}
